// Design tokens extracted from the 21 duplicated getStyles() functions.
// Change a colour here and it changes everywhere.

// Brand colours — identical in light and dark mode.
export const brand = {
  primary: '#6B4277',        // liturgical purple: headers, tabs, buttons
  primaryDark: '#4A148C',
  primaryOnDark: '#D4B6E4',  // purple text that stays readable on dark bg
  primarySoft: '#F3E5F5',
  logo: '#311B92',

  success: '#38A169',
  error: '#E53E3E',
  danger: '#D32F2F',
  dangerDark: '#B71C1C',
  warning: '#F57C00',
  info: '#1976D2',
  income: '#2E7D32',
  expense: '#D32F2F'
};

export const spacing = {
  xs: '4px', sm: '8px', md: '12px', lg: '16px', xl: '20px', xxl: '24px'
};

export const radius = {
  sm: '6px', md: '8px', lg: '10px', xl: '12px', pill: '999px', circle: '50%'
};

// One scale, sized for a data-dense screen: 14px is the reading size, 12px
// is for labels and secondary lines, 11px only for tags.
export const fontSize = {
  xs: '11px', sm: '12px', base: '13px', md: '14px', lg: '16px',
  xl: '18px', xxl: '20px', display: '26px'
};

// "bold" is 600 on purpose: the heavier weights turned every label into a
// headline, so nothing on a page stood out.
export const fontWeight = {
  normal: '400', medium: '500', bold: '600', black: '700'
} as const;

export const zIndex = {
  dropdown: 10, navbar: 1000, drawer: 1001, notification: 9999
};

// #RRGGBB -> rgba(). Used to derive soft tints from a module's accent
// instead of hand-picking a pastel per module.
export const hexToRgba = (hex: string, alpha: number): string => {
  const clean = String(hex).replace('#', '');
  const int = parseInt(clean.length === 3 ? clean.replace(/./g, '$&$&') : clean, 16);
  const r = (int >> 16) & 255;
  const g = (int >> 8) & 255;
  const b = int & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// Mix a colour toward white. Deep accents (#1B5E20, #0D47A1) are unreadable
// on a dark surface, so dark mode uses a lightened variant.
export const lighten = (hex: string, amount: number): string => {
  const clean = String(hex).replace('#', '');
  const int = parseInt(clean.length === 3 ? clean.replace(/./g, '$&$&') : clean, 16);
  const mix = (channel: number) => Math.round(channel + (255 - channel) * amount);
  const r = mix((int >> 16) & 255);
  const g = mix((int >> 8) & 255);
  const b = mix(int & 255);
  return `#${[r, g, b].map((c) => c.toString(16).padStart(2, '0')).join('')}`;
};

// Mode-dependent tokens.
// `accent` overrides the brand purple so each module keeps its own identity
// (Attendance orange, Assets teal, Pastor History crimson, ...).
export const getTokens = (isDarkMode: boolean, accent?: string) => {
  const primary = accent || brand.primary;
  // Readable version of the accent for text/icons on the current surface.
  const primaryText = isDarkMode ? lighten(primary, 0.55) : primary;

  return {
  isDarkMode,
  ...brand,
  // The money and status colours were "identical in light and dark mode",
  // but a dark green (#2E7D32) or red (#D32F2F) figure on a near-black card
  // reads at barely 3:1 — amounts, variances and overdue badges went dim in
  // dark mode (CHM-018 / 022 / 066). Lifted a step so they sit at 4.5:1+ on
  // the dark surfaces; light mode is unchanged.
  ...(isDarkMode ? {
    success: '#4ADE80',
    error: '#F87171',
    danger: '#EF5350',
    warning: '#FBBF24',
    info: '#60A5FA',
    income: '#4ADE80',
    expense: '#F87171'
  } : {}),
  spacing, radius, fontSize, fontWeight, zIndex,

  // Module accent — defaults to the brand purple.
  primary,
  primaryText,
  primarySoft: hexToRgba(primary, isDarkMode ? 0.22 : 0.12),
  primaryBorder: hexToRgba(primary, isDarkMode ? 0.45 : 0.4),

  // Surfaces. A quiet neutral behind white cards — the content carries the
  // colour, the page does not.
  bgApp: isDarkMode ? '#0F1115' : '#F3F4F6',
  bgMain: isDarkMode ? '#181B21' : '#FFFFFF',
  bgSubtle: isDarkMode ? '#1E2229' : '#F8F9FB',
  bgRaised: isDarkMode ? '#232830' : '#FFFFFF',
  bgHover: isDarkMode ? '#232830' : '#F5F6F8',
  bgInput: isDarkMode ? '#1E2229' : '#FFFFFF',
  bgMuted: isDarkMode ? '#2A2F38' : '#EEF0F3',
  bgAccent: hexToRgba(primary, isDarkMode ? 0.2 : 0.1),

  // Text — a neutral ink; the brown of the old palette tinted every screen.
  textMain: isDarkMode ? '#E6E8EC' : '#1F2937',
  textMuted: isDarkMode ? '#9CA3AF' : '#5B6472',
  textFaint: isDarkMode ? '#6B7280' : '#9AA3AF',
  textOnPrimary: '#FFFFFF',
  textAccent: primaryText,

  // Lines
  border: isDarkMode ? '#2A2F38' : '#E5E7EB',
  borderStrong: isDarkMode ? '#3A4150' : '#CBD2DB',
  borderDashed: isDarkMode ? '#242931' : '#EEF0F3',

  // Depth — cards sit on a hairline and a whisper of shadow, nothing more.
  shadowCard: isDarkMode ? '0 1px 2px rgba(0,0,0,0.5)' : '0 1px 2px rgba(16,24,40,0.05), 0 1px 3px rgba(16,24,40,0.06)',
  shadowRaised: isDarkMode ? '0 4px 14px rgba(0,0,0,0.5)' : '0 4px 14px rgba(16,24,40,0.08)',
  // Kept for the screens that still reference them; both now mean "flat".
  shadow3d: 'none',
  border3d: `1px solid ${isDarkMode ? '#2A2F38' : '#E5E7EB'}`,
  overlay: 'rgba(15,17,21,0.55)'
  };
};

export default getTokens;

/** The full token set for one (mode, accent) pair. */
export type Tokens = ReturnType<typeof getTokens>;
